import i18n from "i18next";
import { useSelector } from "react-redux";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const getRndInteger = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

/**
 * Shuffles array in place. ES6 version
 * @param {Array} a items An array containing the items.
 */
const shuffle = (a) => {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const getOrderStatusName = (status) => {
  switch (status) {
    case "processing":
      return i18n.t("myAccount.processing");
    case "confirmed":
      return i18n.t("myAccount.confirmed");
    case "completed":
      return i18n.t("myAccount.completed");
    case "cancelled":
      return i18n.t("myAccount.cancelled");
    default:
      return status;
  }
};

const getOrderPaymentStatusName = (status) => {
  switch (status) {
    case "paid":
      return i18n.t("myAccount.paid");
    case "not_paid":
      return i18n.t("myAccount.notPaid");
    default:
      return status;
  }
};

const getOrderShipmentStatusName = (status) => {
  switch (status) {
    case "pending create":
      return i18n.t("myAccount.pendingCreate");
    case "created":
    // fall through
    case "pending":
    // fall through
    case "shipped":
      return i18n.t("myAccount.shipped");
    case "in transit":
      return i18n.t("myAccount.inTransit");
    case "received":
    // fall through
    case "completed":
      return i18n.t("myAccount.completed");
    case "cancelled":
    // fall through
    case "returned":
    // fall through
    case "frozen":
    // fall through
    case "error":
      return i18n.t("myAccount.error");
    case "N/A":
      return i18n.t("myAccount.na");
    default:
      return status;
  }
};

const getMultiLangProperty = (item, lang, defaultLang = "en") => {
  if (typeof item === "string") return item;
  if (typeof item === "undefined") return "";
  if (typeof item !== "object") return "";
  if (typeof item[lang.toLowerCase()] !== "undefined")
    return item[lang.toLowerCase()];
  if (typeof item[defaultLang] !== "undefined") return item[defaultLang];
  const anyLanguage = Object.values(item).find((val) => !!val);
  if (anyLanguage) return anyLanguage;
  return "";
};

const validateEmail = (email) => {
  const re =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

const validatePassword = (password) => {
  const str = String(password);
  return (
    str.match(/[a-z]/g) &&
    str.match(/[A-Z]/g) &&
    str.match(/[0-9]/g) &&
    // str.match(/[^a-zA-Z\d]/g) &&
    str.length >= 8
  );
};

const validatePhoneNumber = (phone) => {
  const str = String(phone);
  return str.match(/^[2-9][0-9]{7}$/g);
};

const getFormattedPrice = (price) => {
  if (typeof price === "undefined" || price === null || price === "") {
    return "-";
  }

  return `$${new Intl.NumberFormat().format(price)}`;

  // let priceFormat = price.toString();
  //
  // if (price >= 1000 && priceFormat.length === 4) {
  //   priceFormat = priceFormat.replace(priceFormat[0], `${priceFormat[0]},`);
  // } else if (price >= 1000 && priceFormat.length === 5) {
  //   priceFormat = priceFormat.replace(priceFormat[1], `${priceFormat[1]},`);
  // }

  // return `$${priceFormat}`;
};

const getPurchasedItemStatusName = (status) => {
  switch (status) {
    case "pending":
      return "bookingConfirmation.pending";
    case "redeemed":
      return "bookingConfirmation.redeemed";
    case "revoked":
      return "bookingConfirmation.revoked";
    case "expired":
      return "bookingConfirmation.expired";
    default:
      return status;
  }
};

const getWalletHistoryEventName = (type) => {
  switch (type) {
    case "adjust":
      return "myAccount.adjust";
    case "pay":
      return "myAccount.pay";
    case "topup":
      return "myAccount.topup";
    case "rebate":
      return "myAccount.rebate";
    case "event":
      return "myAccount.event";
    default:
      return "N/A";
  }
};

const getRewardHistoryEventName = (type) => {
  switch (type) {
    case "welcome":
      return "myAccount.welcome";
    case "adjust":
      return "myAccount.adjust";
    case "firstPurchase":
      return "myAccount.firstPurchase";
    case "dailyLogin":
      return "myAccount.dailyLogin";
    case "referral":
      return "myAccount.referral";
    case "convert":
      return "myAccount.convert";
    default:
      return "N/A";
  }
};

const truncate = (str, maxLength) =>
  str && str.length > maxLength ? `${str.substring(0, maxLength - 3)}...` : str;

function deleteSameRecord(wholeSearchItemArray, deleteValue) {
  const index = wholeSearchItemArray.indexOf(deleteValue);
  if (index > -1) {
    wholeSearchItemArray.splice(index, 1);
  }
  return wholeSearchItemArray;
}

const setLocalSearchHistory = (searchValue) => {
  let wholeSearchItemArray = [];

  const searchHistoryRecord = JSON.parse(
    localStorage.getItem("searchKeywordHistory")
  );

  searchHistoryRecord &&
    searchHistoryRecord.forEach((record) => {
      wholeSearchItemArray.push(record);
    });

  wholeSearchItemArray = deleteSameRecord(wholeSearchItemArray, searchValue);

  wholeSearchItemArray.push(searchValue);

  localStorage.setItem(
    "searchKeywordHistory",
    JSON.stringify(wholeSearchItemArray)
  );
};

const unescapeHTML = (escapedHTML) => {
  let html = "";
  html = escapedHTML
    .replace(/\{\{iframe/g, "<iframe")
    .replace(/\/}}/g, "></iframe>");
  return html;
};

function extractHTMLContent(s) {
  const span = document.createElement("span");
  span.innerHTML = s;
  return span.textContent || span.innerText;
}

const useSharePreview = (title, description, image) => {
  const { i18n: { language } = {} } = useTranslation();
  const storeConfig = useSelector((state) => state.storeConfig);

  const [metaImageSize, setMetaImageSize] = useState({});

  const getImageMeta = (url) => {
    const img = new Image();
    img.addEventListener("load", () => {
      setMetaImageSize({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    });
    img.src = url;
  };

  const metaTitle = useMemo(
    () =>
      `${getMultiLangProperty(storeConfig?.siteTitle, language)}${
        title ? ` | ${title}` : ""
      }`,
    [language, storeConfig, title]
  );

  const metaTags = useMemo(() => {
    const ogTags = [
      {
        name: "og:title",
        content: metaTitle,
      },
      {
        name: "og:description",
        content: extractHTMLContent(description) || metaTitle,
      },
      {
        name: "og:url",
        content: window.location.href,
      },
      {
        name: "og:type",
        content: "article",
      },
      {
        name: "og:site_name",
        content: getMultiLangProperty(storeConfig?.siteTitle, language),
      },
    ];
    if (image) {
      ogTags.push({
        name: "og:image",
        content: image,
      });
      if (!metaImageSize?.width || !metaImageSize?.height) getImageMeta(image);
    }
    if (metaImageSize?.width && metaImageSize?.height) {
      ogTags.push({
        name: "og:image:width",
        content: metaImageSize?.width,
      });
      ogTags.push({
        name: "og:image:height",
        content: metaImageSize?.height,
      });
    }
    return ogTags;
  }, [description, image, language, metaTitle, metaImageSize, storeConfig]);

  return {
    metaTitle,
    metaTags,
  };
};

export {
  getRndInteger,
  shuffle,
  getOrderStatusName,
  getOrderPaymentStatusName,
  getOrderShipmentStatusName,
  getMultiLangProperty,
  validateEmail,
  validatePassword,
  validatePhoneNumber,
  getFormattedPrice,
  getPurchasedItemStatusName,
  getWalletHistoryEventName,
  getRewardHistoryEventName,
  truncate,
  setLocalSearchHistory,
  unescapeHTML,
  useSharePreview,
};
